import { useState, useEffect, useRef } from "react";

// New site / site settings window of the project manager view: name, which source names feed the site, who can
// see it and (for a new site) the client's cost list. Associations are optional and can be changed here later.
const API = "/api";
const ROLE_LABELS = { site_lead: "Objektijuht", operator: "Operaator" };

function SourcePicker({ c, title, hint, items, selected, onChange, site }) {
  const [q, setQ] = useState("");
  const [manual, setManual] = useState("");
  const lower = (site || "").trim().toLowerCase();
  const own = it => it.site && lower && (it.site.toLowerCase() === lower || it.site.toLowerCase().startsWith(lower + " "));
  const extra = selected.filter(n => !items.some(it => it.name === n)).map(n => ({ name: n, records: 0, db: true, manual: true }));
  const all = [...extra, ...items]
    .filter(it => !q || it.name.toLowerCase().includes(q.toLowerCase()))
    // Free names first (most loads first), then this site's, then the ones another site already uses.
    .sort((a, b) => (a.site && !selected.includes(a.name) ? 1 : 0) - (b.site && !selected.includes(b.name) ? 1 : 0));
  const toggle = n => onChange(selected.includes(n) ? selected.filter(x => x !== n) : [...selected, n]);
  const addManual = () => {
    const n = manual.replace(/\s+/g, " ").trim();
    if (n && !selected.includes(n)) onChange([...selected, n]);
    setManual("");
  };
  const input = { height: 34, padding: "0 10px", fontSize: 13, color: c.text, background: c.inBg, border: `1px solid ${c.inBorder}`,
    borderRadius: 8, fontFamily: "inherit", boxSizing: "border-box" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 14, fontWeight: 600 }}>{title}</span>
        <span style={{ fontSize: 12, color: c.muted }}>{hint}</span>
      </div>
      {items.length > 6 && <input value={q} onChange={e => setQ(e.target.value)} placeholder="Otsi…" style={input} />}
      <div style={{ display: "flex", flexDirection: "column", border: `1px solid ${c.rowBorder}`, borderRadius: 10, maxHeight: 220, overflowY: "auto" }}>
        {all.length === 0 && <div style={{ padding: "10px 12px", fontSize: 13, color: c.muted }}>
          {items.length ? "Otsingule ei vasta ükski nimi" : "Ühtegi nime pole veel nähtud. Lisa nimi käsitsi, kui see on teada."}</div>}
        {all.map((it, i) => {
          const checked = selected.includes(it.name) || (!it.db && own(it));
          const locked = (!it.db && it.site) || (it.site && !own(it) && !selected.includes(it.name));
          return (
            <label key={it.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", fontSize: 13,
              borderTop: i ? `1px solid ${c.rowBorder}` : "none", cursor: locked ? "default" : "pointer", opacity: locked && !checked ? 0.55 : 1 }}>
              <input type="checkbox" checked={checked} disabled={!!locked} onChange={() => toggle(it.name)} />
              <span style={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>{it.name}</span>
              <span style={{ fontSize: 12, color: c.muted, whiteSpace: "nowrap" }}>
                {!it.db && it.site ? `${it.site} · seadistatud serveris`
                  : it.site && !own(it) ? `→ ${it.site}`
                  : it.manual ? "käsitsi lisatud"
                  : it.records ? `${it.records} kirjet${it.last_seen ? ` · ${it.last_seen}` : ""}` : ""}
              </span>
            </label>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <input value={manual} onChange={e => setManual(e.target.value)} placeholder="Nimi täpselt nagu allikas, kui seda loendis pole"
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addManual(); } }} style={{ ...input, flex: 1, minWidth: 0 }} />
        <button type="button" onClick={addManual} disabled={!manual.trim()} style={{ ...input, cursor: "pointer", fontWeight: 600 }}>Lisa</button>
      </div>
    </div>
  );
}

export default function SiteSetupModal({ c, mobile, site, onClose, onDone }) {
  const editing = !!site;
  const [opts, setOpts] = useState(null);
  const [name, setName] = useState("");
  const [waybiller, setWaybiller] = useState([]);
  const [infrafly, setInfrafly] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [userQ, setUserQ] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const fileInput = useRef(null);

  useEffect(() => {
    const q = site ? `?site=${encodeURIComponent(site)}` : "";
    fetch(`${API}/admin/site-setup${q}`, { credentials: "same-origin" })
      .then(async r => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.detail || `Viga ${r.status}`);
        setOpts(j);
        if (j.current) {
          setWaybiller(j.current.waybiller);
          setInfrafly(j.current.infrafly);
          setUserIds(j.current.user_ids);
        }
      })
      .catch(e => setError(e.message));
  }, [site]);

  useEffect(() => {
    const onKey = e => e.key === "Escape" && !busy && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const siteName = editing ? site : name.replace(/\s+/g, " ").trim();
  const nameIssue = (() => {
    if (editing || !opts || !siteName) return "";
    const n = siteName.toLowerCase();
    const same = opts.sites.find(s => s.toLowerCase() === n);
    if (same) return `Objekt ${same} on juba olemas`;
    const clash = opts.sites.find(s => n.startsWith(s.toLowerCase() + " ") || s.toLowerCase().startsWith(n + " "));
    return clash ? `Nimi ei tohi alata objekti „${clash}” nimega (ega olla selle algus), muidu segunevad andmed` : "";
  })();
  const users = (opts?.users || []).filter(u => !userQ || u.name.toLowerCase().includes(userQ.toLowerCase()));
  const sourcesBefore = opts?.current ? [...opts.current.waybiller, ...opts.current.infrafly] : [];
  const newSources = [...waybiller, ...infrafly].some(n => !sourcesBefore.includes(n));

  const submit = async () => {
    setError("");
    setBusy(editing ? "Salvestan…" : "Loon objekti…");
    try {
      const body = { name: siteName, waybiller, infrafly, user_ids: userIds };
      const r = await fetch(editing ? `${API}/admin/sites/${encodeURIComponent(site)}` : `${API}/admin/sites`, {
        method: editing ? "PUT" : "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || `Viga ${r.status}`);
      let uploadError = "";
      if (file) {
        setBusy("Loen pakkumist…");
        const fd = new FormData();
        fd.append("file", file);
        const u = await fetch(`${API}/pm/offer/upload?site=${encodeURIComponent(j.name)}`, { method: "POST", credentials: "same-origin", body: fd });
        if (!u.ok) uploadError = (await u.json().catch(() => ({}))).detail || `Viga ${u.status}`;
      }
      onDone({ name: j.name, created: !editing, uploaded: !!file && !uploadError, uploadError, backfill: j.backfill });
    } catch (e) {
      setError(e.message);
      setBusy("");
    }
  };

  const sectionTitle = { fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: c.muted };
  const pad = mobile ? "16px" : "20px 24px";
  const input = { height: 40, padding: "0 12px", fontSize: 14, color: c.text, background: c.inBg, border: `1px solid ${c.inBorder}`,
    borderRadius: 9, fontFamily: "inherit", boxSizing: "border-box", width: "100%" };
  const button = { height: 40, padding: "0 18px", fontSize: 14, fontWeight: 600, borderRadius: 9, cursor: "pointer", fontFamily: "inherit" };

  return (
    <div onClick={() => !busy && onClose()} style={{ position: "fixed", inset: 0, zIndex: 9999, background: c.overlay, display: "flex",
      alignItems: "center", justifyContent: "center", padding: mobile ? 8 : 24, boxSizing: "border-box" }}>
      <form onClick={e => e.stopPropagation()} onSubmit={e => { e.preventDefault(); submit(); }} role="dialog"
        aria-label={editing ? "Objekti seaded" : "Uus objekt"} style={{
          width: 720, maxWidth: "100%", maxHeight: "92vh", display: "flex", flexDirection: "column", boxSizing: "border-box",
          background: c.card, border: `1px solid ${c.inBorder}`, borderRadius: 14, color: c.text,
        }}>
        <div style={{ padding: pad, borderBottom: `1px solid ${c.rowBorder}`, display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={sectionTitle}>{editing ? "Objekti seaded" : "Uus objekt"}</span>
          <span style={{ fontSize: 19, fontWeight: 700 }}>{editing ? site : "Uue pakkumise objekt"}</span>
        </div>

        <div style={{ padding: pad, overflowY: "auto", display: "flex", flexDirection: "column", gap: 22 }}>
          {!opts && !error && <div style={{ fontSize: 13, color: c.muted }}>Laen…</div>}
          {opts && <>
            {!editing && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <span style={sectionTitle}>1 · Nimi</span>
                <input autoFocus value={name} onChange={e => setName(e.target.value)} maxLength={100} placeholder="nt Rakvere ringristmik" style={input} />
                {nameIssue && <span style={{ fontSize: 12, color: c.warn }}>{nameIssue}</span>}
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <span style={sectionTitle}>{editing ? "Andmeallikad" : "2 · Andmeallikad (valikuline)"}</span>
              <span style={{ fontSize: 12, color: c.muted, marginTop: -8 }}>
                Millised nimed allikasüsteemides kuuluvad sellele objektile. Saab hiljem muuta: Projektijuht → ⚙ Objekti seaded.
              </span>
              <SourcePicker c={c} title="Waybiller" hint="saatelehe sihtkoht" items={opts.names.waybiller} site={siteName}
                selected={waybiller} onChange={setWaybiller} />
              <SourcePicker c={c} title="Infrafly" hint="masinatundide objekt" items={opts.names.infrafly} site={siteName}
                selected={infrafly} onChange={setInfrafly} />
              <span style={{ fontSize: 12, color: c.muted }}>
                Verston: kogu konto kuulub objektile {opts.verston_site}; teise objekti jaoks on vaja eraldi kontot.
                {newSources && ` Uute nimede veod tuuakse ${opts.backfill_days} päeva tagant järele mõne minuti jooksul pärast salvestamist.`}
              </span>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <span style={sectionTitle}>{editing ? "Ligipääs" : "3 · Ligipääs"}</span>
              <span style={{ fontSize: 12, color: c.muted, marginTop: -2 }}>Adminid näevad kõiki objekte; vali, kes teistest seda näevad.</span>
              {opts.users.length > 8 && <input value={userQ} onChange={e => setUserQ(e.target.value)} placeholder="Otsi kasutajat…" style={{ ...input, height: 34, fontSize: 13 }} />}
              <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", border: `1px solid ${c.rowBorder}`, borderRadius: 10,
                maxHeight: 220, overflowY: "auto" }}>
                {users.length === 0 && <div style={{ padding: "10px 12px", fontSize: 13, color: c.muted }}>Kasutajaid pole</div>}
                {users.map(u => (
                  <label key={u.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", fontSize: 13, cursor: "pointer",
                    borderBottom: `1px solid ${c.rowBorder}` }}>
                    <input type="checkbox" checked={userIds.includes(u.id)}
                      onChange={() => setUserIds(ids => (ids.includes(u.id) ? ids.filter(x => x !== u.id) : [...ids, u.id]))} />
                    <span style={{ flex: 1, minWidth: 0 }}>{u.name}</span>
                    <span style={{ fontSize: 12, color: c.muted }}>{ROLE_LABELS[u.role] || u.role}</span>
                  </label>
                ))}
              </div>
            </div>

            {!editing && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <span style={sectionTitle}>4 · Pakkumine (valikuline)</span>
                <input ref={fileInput} type="file" accept=".xlsx,.xlsm" style={{ display: "none" }}
                  onChange={e => { const f = e.target.files[0]; e.target.value = ""; if (f) setFile(f); }} />
                <div onClick={() => fileInput.current.click()} onDragOver={e => e.preventDefault()}
                  onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setFile(f); }}
                  style={{ border: `1.5px dashed ${file ? c.accent : c.inBorder}`, borderRadius: 10, padding: "16px", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 12, fontSize: 13 }}>
                  <svg width="18" height="18" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 9.5V2.5M4 5l3-3 3 3M2.5 11.5h9" /></svg>
                  <span style={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
                    {file ? file.name : "Lohista kliendi kululoend (.xlsx) siia või klõpsa valimiseks"}
                  </span>
                  {file && <button type="button" onClick={e => { e.stopPropagation(); setFile(null); }}
                    style={{ background: "none", border: "none", color: c.muted, cursor: "pointer", fontSize: 18 }} aria-label="Eemalda fail">×</button>}
                </div>
              </div>
            )}
          </>}
        </div>

        <div style={{ padding: pad, borderTop: `1px solid ${c.rowBorder}`, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {error && <span style={{ flex: "1 1 100%", fontSize: 13, color: c.error }}>{error}</span>}
          <div style={{ flex: 1 }} />
          <button type="button" onClick={onClose} disabled={!!busy}
            style={{ ...button, background: "transparent", color: c.text, border: `1px solid ${c.inBorder}` }}>Tühista</button>
          <button type="submit" disabled={!opts || !!busy || (!editing && (!siteName || !!nameIssue))}
            style={{ ...button, background: c.accent, color: c.onAccent, border: `1px solid ${c.accent}`,
              opacity: !opts || busy || (!editing && (!siteName || nameIssue)) ? 0.5 : 1 }}>
            {busy || (editing ? "Salvesta" : file ? "Loo objekt ja loe pakkumine" : "Loo objekt")}
          </button>
        </div>
      </form>
    </div>
  );
}
